import { Link } from 'react-router-dom';
import { Briefcase, Users, Target, Award, Globe, Heart } from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-3">
              <Briefcase className="w-7 h-7 text-blue-900" />
              <span className="text-xl font-bold text-blue-900">JobSuiza</span>
            </Link>
            <Link to="/" className="text-blue-900 hover:text-blue-700 font-medium">
              Volver al inicio
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-emerald-700 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Quiénes Somos</h1>
          <p className="text-xl text-blue-100">
            Conectando talento hispanohablante con oportunidades en Suiza
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Nuestra Historia */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
            <Heart className="w-6 h-6 text-red-500 mr-3" />
            Nuestra Historia
          </h2>
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-700 leading-relaxed mb-4">
              JobSuiza nació de una necesidad real. Como hispanohablantes que emigramos a Suiza, 
              experimentamos de primera mano los desafíos de buscar empleo en un país con cuatro idiomas 
              oficiales, donde encontrar trabajos que valoren nuestro español era como buscar una aguja en un pajar.
            </p>
            <p className="text-slate-700 leading-relaxed mb-4">
              Descubrimos que muchas empresas suizas buscan activamente profesionales que hablen español, 
              especialmente en sectores como turismo, hostelería, atención al cliente, comercio internacional 
              y servicios financieros. Sin embargo, estas ofertas estaban dispersas en múltiples portales y 
              a menudo eran difíciles de encontrar.
            </p>
            <p className="text-slate-700 leading-relaxed">
              Así nació JobSuiza en 2024: una plataforma especializada que reúne todas las ofertas de 
              empleo en Suiza que requieren o valoran el español, facilitando la búsqueda laboral para 
              nuestra comunidad hispanohablante.
            </p>
          </div>
        </section>

        {/* Nuestra Misión */}
        <section className="mb-12 bg-slate-50 rounded-xl p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
            <Target className="w-6 h-6 text-emerald-600 mr-3" />
            Nuestra Misión
          </h2>
          <p className="text-slate-700 leading-relaxed text-lg">
            Facilitar la integración laboral de hispanohablantes en Suiza, conectando el talento 
            de nuestra comunidad con empresas que valoran la diversidad lingüística y cultural. 
            Queremos ser el puente entre las oportunidades suizas y los profesionales de habla hispana.
          </p>
        </section>

        {/* Qué Ofrecemos */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
            <Award className="w-6 h-6 text-yellow-500 mr-3" />
            Qué Ofrecemos
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 rounded-lg p-6">
              <h3 className="font-semibold text-slate-900 mb-2">Ofertas Seleccionadas</h3>
              <p className="text-slate-600 text-sm">
                Agregamos ofertas de trabajo de múltiples fuentes, filtrando específicamente 
                aquellas que requieren o valoran el español.
              </p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-6">
              <h3 className="font-semibold text-slate-900 mb-2">Alertas Personalizadas</h3>
              <p className="text-slate-600 text-sm">
                Configura alertas según tu sector, ubicación y nivel de español para recibir 
                ofertas relevantes directamente en tu email.
              </p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-6">
              <h3 className="font-semibold text-slate-900 mb-2">Blog Informativo</h3>
              <p className="text-slate-600 text-sm">
                Artículos sobre vida en Suiza, consejos laborales, trámites de permisos 
                y guías para hispanohablantes.
              </p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-6">
              <h3 className="font-semibold text-slate-900 mb-2">Tablón de Anuncios</h3>
              <p className="text-slate-600 text-sm">
                Espacio comunitario donde usuarios pueden ofrecer y buscar servicios 
                entre la comunidad hispanohablante.
              </p>
            </div>
          </div>
        </section>

        {/* Nuestros Valores */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
            <Users className="w-6 h-6 text-blue-600 mr-3" />
            Nuestros Valores
          </h2>
          <div className="space-y-4">
            <div className="flex items-start space-x-4">
              <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2"></div>
              <div>
                <h3 className="font-semibold text-slate-900">Comunidad</h3>
                <p className="text-slate-600 text-sm">
                  Creemos en el poder de la comunidad hispanohablante en Suiza y trabajamos 
                  para fortalecerla.
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2"></div>
              <div>
                <h3 className="font-semibold text-slate-900">Transparencia</h3>
                <p className="text-slate-600 text-sm">
                  Todas las ofertas provienen de fuentes verificadas y mostramos claramente 
                  la información disponible.
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2"></div>
              <div>
                <h3 className="font-semibold text-slate-900">Accesibilidad</h3>
                <p className="text-slate-600 text-sm">
                  Nuestro servicio básico es gratuito porque creemos que todos merecen 
                  acceso a oportunidades laborales.
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2"></div>
              <div>
                <h3 className="font-semibold text-slate-900">Calidad</h3>
                <p className="text-slate-600 text-sm">
                  Nos esforzamos por ofrecer ofertas relevantes y contenido útil que 
                  realmente ayude a nuestra comunidad.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Alcance */}
        <section className="mb-12 bg-blue-50 rounded-xl p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
            <Globe className="w-6 h-6 text-blue-600 mr-3" />
            Nuestro Alcance
          </h2>
          <p className="text-slate-700 leading-relaxed mb-4">
            JobSuiza opera desde Suiza y está enfocado en servir a la comunidad hispanohablante 
            que reside o desea trabajar en territorio suizo. Cubrimos ofertas en todos los 
            cantones, con especial énfasis en las principales ciudades:
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            {['Zúrich', 'Ginebra', 'Basilea', 'Berna', 'Lausana', 'Lucerna', 'Lugano', 'St. Gallen'].map(city => (
              <span key={city} className="px-3 py-1 bg-white text-slate-700 rounded-full text-sm border border-slate-200">
                {city}
              </span>
            ))}
          </div>
        </section>

        {/* Contacto */}
        <section className="text-center py-8 border-t border-slate-200">
          <h2 className="text-xl font-bold text-slate-900 mb-4">¿Tienes preguntas?</h2>
          <p className="text-slate-600 mb-6">
            Estamos aquí para ayudarte. No dudes en contactarnos.
          </p>
          <Link 
            to="/contacto" 
            className="inline-flex items-center px-6 py-3 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition-colors font-medium"
          >
            Contactar
          </Link>
        </section>
      </div>
    </div>
  );
};

export default AboutPage;
